import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import { Avatar, AvatarGroup, Badge, Box, ListItemAvatar, ListItemButton, ListItemText, Tooltip, Typography } from "@mui/material";
import { colors } from "../../../theme/muiColors";
import { grey } from "../../../theme/muiColors";
import { MessageGroupSummary } from "../messageGroups.types";
import { getAvatarColor, getGroupInitials, getMemberFullName, getMemberRoleLabel } from "../utils/messageGroupDisplay.utils";

const formatConversationDate = (date?: string | null) => {
  if (!date) return "";
  const value = new Date(date);
  const now = new Date();
  const isToday = value.toDateString() === now.toDateString();
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = value.toDateString() === yesterday.toDateString();

  if (isToday) return value.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isYesterday) return "Yesterday";
  return value.toLocaleDateString([], { month: "short", day: "numeric" });
};

interface MessageGroupListItemProps {
  group: MessageGroupSummary;
  isSelected: boolean;
  isEmployee?: boolean;
  onSelect: (group: MessageGroupSummary) => void;
}

export function MessageGroupListItem({ group, isSelected, isEmployee = false, onSelect }: MessageGroupListItemProps) {
  const unreadCount = group.unreadCount ?? 0;
  const hasUnread = unreadCount > 0 && !isSelected;
  const lastMessageDate = formatConversationDate(group.lastMessageAt);
  const members = isEmployee ? group.members.filter((member) => member.type === "workforce") : group.members;
  const isOwnNumber = (member: { phoneNumber?: string | null }) =>
    Boolean(group.smsFromNumber && member.phoneNumber && member.phoneNumber === group.smsFromNumber);

  const preview = group.lastMessage
    ? group.lastMessageSenderName
      ? `${group.lastMessageSenderName.split(" ")[0]}: ${group.lastMessage}`
      : group.lastMessage
    : "No messages yet";

  const membersTooltip = (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Members</Typography>
      {members.map((member) => {
        const name = isOwnNumber(member) ? "You" : getMemberFullName(member) || "Unknown";
        const role = isOwnNumber(member) ? null : getMemberRoleLabel(member);
        return (
          <Typography key={`${member.type}:${member.uid}`} sx={{ fontSize: 11 }}>
            {role ? `${name} (${role})` : name}
          </Typography>
        );
      })}
    </Box>
  );

  return (
    <ListItemButton
      onClick={() => onSelect(group)}
      sx={{
        py: 1.15,
        px: 1.5,
        mb: 0.6,
        borderRadius: "14px",
        backgroundColor: isSelected ? colors.blue[50] : "transparent",
        transition: "all 0.2s",
        display: "flex",
        alignItems: "flex-start",
        "&:hover": { bgcolor: isSelected ? colors.blue[50] : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.035)" },
      }}
    >
      <ListItemAvatar sx={{ minWidth: 45, pt: 0.2 }}>
        <Badge
          color="error"
          variant="dot"
          invisible={!hasUnread}
          overlap="circular"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Avatar
            src={group.avatar || undefined}
            alt={group.name}
            sx={{
              width: 34,
              height: 34,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: group.avatar ? undefined : isSelected ? colors.blue[500] : colors.blue[50],
              color: group.avatar ? undefined : isSelected ? "white" : colors.blue[600],
            }}
          >
            {group.avatar ? null : getGroupInitials(group.name)}
          </Avatar>
        </Badge>
      </ListItemAvatar>

      <ListItemText
        disableTypography
        sx={{ my: 0, minWidth: 0 }}
        primary={
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Typography
              component="span"
              noWrap
              sx={{
                flex: 1,
                fontSize: "14px",
                fontWeight: hasUnread ? 800 : 600,
                color: isSelected ? colors.blue[900] : "text.primary",
              }}
            >
              {group.name}
            </Typography>
            {group.mentionsMe && (
              <Tooltip title="You were mentioned" arrow>
                <AlternateEmailRoundedIcon sx={{ fontSize: 14, color: colors.blue[700], flexShrink: 0 }} />
              </Tooltip>
            )}
            {lastMessageDate ? (
              <Typography
                component="span"
                sx={{
                  fontSize: "11px",
                  color: hasUnread ? colors.blue[700] : "text.secondary",
                  whiteSpace: "nowrap",
                  fontWeight: hasUnread ? 700 : 400,
                }}
              >
                {lastMessageDate}
              </Typography>
            ) : null}
          </Box>
        }
        secondary={
          <Box component="span" sx={{ minWidth: 0, display: "block" }}>
            <Typography
              component="span"
              noWrap
              sx={{
                display: "block",
                fontSize: "12px",
                color: hasUnread ? "text.primary" : "text.secondary",
                fontWeight: hasUnread ? 600 : 400,
                lineHeight: 1.35,
              }}
            >
              {preview}
            </Typography>
            <Box component="span" sx={{ mt: 0.3, display: "flex", alignItems: "center", gap: 0.75 }}>
              <Tooltip
                title={membersTooltip}
                placement="top"
                arrow
                enterDelay={150}
                slotProps={{ popper: { sx: { zIndex: "2147483647 !important" } }, tooltip: { sx: { maxWidth: 200 } } }}
              >
                <AvatarGroup
                  max={4}
                  sx={{
                    justifyContent: "flex-start",
                    width: "fit-content",
                    "& .MuiAvatar-root": { width: 18, height: 18, fontSize: "8px", border: "1px solid var(--fx-surface-base, #FFFFFF)" },
                  }}
                >
                  {members.map((member) => {
                    const name = isOwnNumber(member) ? "You" : getMemberFullName(member) || "Unknown";
                    return (
                      <Avatar
                        key={`${member.type}:${member.uid}`}
                        src={member.avatar || undefined}
                        alt={name}
                        sx={{ bgcolor: member.avatar ? undefined : getAvatarColor(name) }}
                      >
                        {name[0]?.toUpperCase() || "?"}
                      </Avatar>
                    );
                  })}
                </AvatarGroup>
              </Tooltip>
              <Typography component="span" sx={{ fontSize: "11px", color: grey[500] }}>
                {group.membersCount} {group.membersCount === 1 ? "member" : "members"}
              </Typography>
            </Box>
          </Box>
        }
      />

      {hasUnread ? (
        <Box
          sx={{
            minWidth: 20,
            height: 20,
            px: 0.7,
            borderRadius: "999px",
            bgcolor: colors.blue[500],
            color: "#fff",
            fontSize: "11px",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: 1,
            mt: 2.3,
          }}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </Box>
      ) : null}
    </ListItemButton>
  );
}
